import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, AlertTriangle, ArrowRight, CheckCircle } from 'lucide-react';

const UrgencyBanner = ({ spotsLeft, deadline }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = deadline - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  const totalSpots = 15;
  const spotsTaken = totalSpots - spotsLeft;
  const percentageTaken = (spotsTaken / totalSpots) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-[#1A2E24] text-white py-3 px-4"
      data-testid="urgency-banner"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          {/* Left: Spots indicator */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#D95A39] rounded-full urgency-pulse" />
              <span className="text-xs md:text-sm font-semibold text-[#D95A39]">
                Acceso Fundadores
              </span>
            </div>
            <span className="text-xs md:text-sm text-white/80">
              Solo quedan <span className="font-bold text-white">{spotsLeft} cupos</span>
            </span>
          </div>

          {/* Center: Progress bar */}
          <div className="hidden md:flex items-center gap-3 flex-1 max-w-xs">
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentageTaken}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="spots-bar h-full rounded-full"
              />
            </div>
            <span className="text-xs text-white/60 whitespace-nowrap">{spotsTaken}/{totalSpots}</span>
          </div>

          {/* Right: Countdown */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-xs md:text-sm">
              <Clock className="w-4 h-4 text-[#849B89]" strokeWidth={1.5} />
              <span className="text-white/80">Cierra en:</span>
              <div className="flex gap-1 font-mono font-bold">
                <span className="bg-white/10 px-1.5 py-0.5 rounded">{String(timeLeft.days).padStart(2, '0')}d</span>
                <span className="bg-white/10 px-1.5 py-0.5 rounded">{String(timeLeft.hours).padStart(2, '0')}h</span>
                <span className="bg-white/10 px-1.5 py-0.5 rounded">{String(timeLeft.minutes).padStart(2, '0')}m</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const WhatsAppMockup = () => {
  const messages = [
    { type: 'ai', text: '¡Buenos días, María! ¿Cómo va tu día? ¿Ya tomaste tu desayuno?', delay: 0.5 },
    { type: 'user', text: 'Hola! Sí, pero me comí unas galletas extra 😅', delay: 1.5 },
    { type: 'ai', text: 'No te preocupes, un desliz no arruina tu progreso. ¿Qué tal si en el almuerzo compensamos con más verduras? Te sugiero una ensalada verde con pollo.', delay: 2.5 },
    { type: 'user', text: 'Buena idea! Gracias por el apoyo 💚', delay: 4 },
  ];

  return (
    <div className="phone-mockup w-full max-w-[280px] md:max-w-[320px] mx-auto">
      <div className="phone-screen">
        {/* WhatsApp Header */}
        <div className="whatsapp-header">
          <div className="w-10 h-10 rounded-full bg-[#2E5C41] flex items-center justify-center">
            <span className="text-white text-sm font-bold">HA</span>
          </div>
          <div className="flex-1">
            <p className="text-white text-sm font-semibold">HabitIA - Dra. García</p>
            <p className="text-white/70 text-xs">en línea</p>
          </div>
        </div>

        {/* Chat Area */}
        <div className="whatsapp-chat">
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: msg.delay, duration: 0.3 }}
              className={`chat-bubble ${msg.type}`}
            >
              {msg.text}
              <span className="text-[10px] text-gray-500 ml-2">
                {msg.type === 'ai' ? '8:00' : '8:02'}
              </span>
            </motion.div>
          ))}
          
          {/* Typing indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 5 }}
            className="chat-bubble ai flex items-center gap-1"
          >
            <motion.span
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="w-2 h-2 bg-gray-400 rounded-full"
            />
            <motion.span
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
              className="w-2 h-2 bg-gray-400 rounded-full"
            />
            <motion.span
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
              className="w-2 h-2 bg-gray-400 rounded-full"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const HeroSection = ({ onCTAClick, spotsLeft, deadline }) => {
  const benefits = [
    'Asistente 24/7 que habla como tú',
    'Seguimiento automatizado vía WhatsApp',
    'Más retención, menos trabajo manual',
  ];

  return (
    <section className="relative min-h-screen pt-16" data-testid="hero-section">
      {/* Urgency Banner */}
      <UrgencyBanner spotsLeft={spotsLeft} deadline={deadline} />

      {/* Hero Content */}
      <div className="relative px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Abstract Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-gradient-to-br from-[#2E5C41]/5 via-[#849B89]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] bg-gradient-to-tr from-[#2E5C41]/5 via-transparent to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#F2F4F0] rounded-full mb-6"
              >
                <span className="w-2 h-2 bg-[#2E5C41] rounded-full animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#4B5E53]">
                  Lanzamiento exclusivo para nutricionistas
                </span>
              </motion.div>

              {/* Headline */}
              <h1 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2E24] tracking-tighter leading-[1.1] mb-6">
                Tu clon de IA que
                <span className="block text-[#2E5C41]">acompaña pacientes</span>
                entre consultas
              </h1>

              {/* Subheadline */}
              <p className="text-base md:text-lg text-[#4B5E53] leading-relaxed mb-8 max-w-xl">
                El 70% de tus pacientes abandonan entre citas. <strong>HabitIA</strong> los acompaña 24/7 vía WhatsApp, habla exactamente como tú, y construye los hábitos que garantizan resultados.
              </p>

              {/* Benefits List */}
              <ul className="space-y-3 mb-8">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-3 text-[#1A2E24]"
                  >
                    <CheckCircle className="w-5 h-5 text-[#2E5C41] flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm md:text-base">{benefit}</span>
                  </motion.li>
                ))}
              </ul>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  onClick={onCTAClick}
                  className="group flex items-center justify-center gap-2 px-8 py-4 bg-[#2E5C41] text-white text-base font-bold rounded-full hover:bg-[#1A2E24] transition-all duration-300"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="cta-hero"
                >
                  Quiero Acceso Fundadores
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </motion.button>
                <a
                  href="https://app.habitai.fit"
                  className="flex items-center justify-center gap-2 px-8 py-4 border border-[#2E5C41]/20 text-[#1A2E24] text-base font-medium rounded-full hover:bg-[#F2F4F0] transition-all duration-300"
                  data-testid="cta-secondary"
                >
                  Probar Demo
                </a>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-[#2E5C41]/10">
                <div className="flex items-center gap-2 text-sm text-[#4B5E53]">
                  <CheckCircle className="w-4 h-4 text-[#2E5C41]" strokeWidth={1.5} />
                  <span>30 días de garantía</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#4B5E53]">
                  <CheckCircle className="w-4 h-4 text-[#2E5C41]" strokeWidth={1.5} />
                  <span>Pago seguro</span>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 30, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="order-1 lg:order-2 flex justify-center lg:justify-end"
            >
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#2E5C41]/10 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#849B89]/10 rounded-full blur-2xl" />
                
                <WhatsAppMockup />
                
                {/* Floating badges */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 }}
                  className="absolute -left-4 top-1/4 bg-white px-3 py-2 rounded-xl shadow-lg border border-[#2E5C41]/10"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center">
                      <Users className="w-4 h-4 text-[#2E5C41]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#1A2E24]">+95%</p>
                      <p className="text-[10px] text-[#4B5E53]">Retención</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.3 }}
                  className="absolute -right-4 bottom-1/4 bg-white px-3 py-2 rounded-xl shadow-lg border border-[#2E5C41]/10"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-[#2E5C41]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#1A2E24]">75+ hrs</p>
                      <p className="text-[10px] text-[#4B5E53]">Ahorradas/mes</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
